/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import { CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import * as actionsProgramacaoDiariaDia from '../../../store/programacaoDiariaDia/actions';
import { PROGRAMACAO_DIARIA_DIA } from '../../../constants/modules';
import { hasModuleConfiguration } from '../../../helpers/moduleConfigurationPermission';
import { isNotAdmin } from 'utils';
import * as Roles from '../../../constants/role';
import Formulario from './Formulario';
import ItensDoDiaCell from './ItensDoDiaCell';
import ResumoDiaModal from './ResumoDiaModal';

const cellContentSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: '100%',
  minWidth: 0,
  width: '100%',
};

const defaultFilter = authData => ({
  inicio: null,
  fim: null,
  usuarioId: isNotAdmin(authData.role) ? [authData.id] : [],
});

const ProgramacaoDiariaDiaV2 = ({
  authData,
  moduleConfigurations,
  history,
  exportProgramacaoDiariaDia,
}) => {
  const canAccess = hasModuleConfiguration(
    moduleConfigurations,
    authData,
    PROGRAMACAO_DIARIA_DIA
  );

  useEffect(() => {
    if (!canAccess) {
      history.replace('/v2/programacao-diaria');
    }
  }, [canAccess, history]);

  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [reload, setReload] = useState(false);
  const [localFilter, setLocalFilter] = useState(defaultFilter(authData));
  const [openFilter, setOpenFilter] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [openResumo, setOpenResumo] = useState(false);
  const [resumoRow, setResumoRow] = useState(null);

  const isAdminUser = !isNotAdmin(authData.role);

  const apiFilter = useMemo(
    () =>
      isAdminUser
        ? { ...localFilter }
        : { ...localFilter, usuarioId: authData?.id ? [authData.id] : [] },
    [isAdminUser, localFilter, authData]
  );

  const onAdd = () => {
    setId(null);
    setOpen(true);
  };

  const onEdit = rowId => {
    setId(rowId);
    setOpen(true);
  };

  const onClose = isSave => {
    setOpen(false);
    setId(null);
    if (isSave) setReload(r => !r);
  };

  const onOpenResumo = row => {
    setResumoRow(row);
    setOpenResumo(true);
  };

  const onCloseResumo = () => {
    setOpenResumo(false);
    setResumoRow(null);
  };

  const columns = [
    {
      field: 'referenceDayFormat',
      headerName: 'Data',
      minWidth: 160,
      flex: 1,
      renderCell: params => (
        <Box sx={cellContentSx}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {params.row.referenceDayFormat || params.row.referenceDay || '-'}
          </Typography>
          {params.row.createdAt && (
            <Typography variant="caption" color="text.secondary">
              Criado em {params.row.createdAt}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'inspetorName',
      headerName: 'Inspetor',
      minWidth: 230,
      flex: 1.3,
      renderCell: params => (
        <Box sx={cellContentSx}>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 700 }}>Inspetor: </Box>
            {params.row.inspetorName || params.row.inspetorNome || '-'}
          </Typography>
          {params.row.inspetorAuxiliarName && (
            <Typography variant="caption" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 700 }}>Ajudante/Observador: </Box>
              {params.row.inspetorAuxiliarName}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'itensResumo',
      headerName: 'Itens do dia',
      minWidth: 280,
      flex: 1.8,
      sortable: false,
      renderCell: params => (
        <ItensDoDiaCell row={params.row} onOpenResumo={onOpenResumo} />
      ),
    },
  ];

  if (!canAccess) {
    return null;
  }

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Typography variant="h4">Programação por Dia</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="programacao-diaria-dia/filter"
          paginationUrl="/v2/programacao-diaria-dia"
          filterData={apiFilter}
          refreshKey={reload}
          rowHeight={96}
          headerActions={[
            { label: 'Novo', onClick: onAdd, startIcon: <AddIcon />, variant: 'contained' },
            {
              label: 'Exportar',
              onClick: () => setOpenExport(true),
              startIcon: <DescriptionOutlinedIcon />,
              variant: 'outlined',
            },
          ]}
          columns={columns}
          onEdit={onEdit}
          emptyMessage="Nenhuma programação do dia encontrada"
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilter(authData)}
          appliedFilter={localFilter}
          fields={[
            { field: 'useDates', useDateTypes: false, useDateTypesReadOnly: true },
            {
              field: 'useUsuarios',
              md: 6,
              xs: 12,
              multiSelect: true,
              disabled: Roles.ADMIN !== authData.role && Roles.SUPER_ADMIN !== authData.role,
            },
            {
              field: 'useOption',
              md: 6,
              xs: 12,
              code: 'TIPO_PROGRAMACAO_DIARIA',
              label: 'Tipo da Programação',
              key: 'tipoIds',
            },
            { field: 'useNsirs', md: 6, xs: 12 },
            { field: 'useMalha', md: 6, xs: 12, multiSelect: true },
            { field: 'useGerencia', md: 6, xs: 12, multiSelect: true },
            { field: 'usePolo', md: 6, xs: 12, multiSelect: true },
            { field: 'useTipoServico', md: 6, xs: 12 },
          ]}
          onFilter={filterData => {
            const next = isAdminUser
              ? filterData
              : { ...filterData, usuarioId: authData?.id ? [authData.id] : [] };
            setLocalFilter(next);
            setOpenFilter(false);
          }}
        />

        <Formulario id={id} open={open} onClose={onClose} />
        <ResumoDiaModal open={openResumo} row={resumoRow} onClose={onCloseResumo} />

        <DialogDefaultV2
          open={openExport}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={() => {
            exportProgramacaoDiariaDia(apiFilter, () => {
              setOpenExport(false);
            });
          }}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>Deseja baixar todas as informações filtradas?</DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

ProgramacaoDiariaDiaV2.propTypes = {
  authData: PropTypes.object.isRequired,
  moduleConfigurations: PropTypes.array,
  history: PropTypes.object.isRequired,
  exportProgramacaoDiariaDia: PropTypes.func.isRequired,
};

ProgramacaoDiariaDiaV2.defaultProps = {
  moduleConfigurations: [],
};

const mapStateToProps = state => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  ...actionsProgramacaoDiariaDia,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ProgramacaoDiariaDiaV2));
